import axios from 'axios'
import { baseUmpPath } from '@root/api/api'
import { getPcekZone } from '@entities/user'
import { getXSystemId } from '@entities/payment'
import { usePartner } from '@shared/lib/partner'
import { useWebView } from '@shared/lib/web-view'
import {
  useInitUtmColizeum,
  useInitUtmIsMkSmsGames,
  useInitUtmMembrana,
  useInitUtmMtsDengi,
  useInitUtmPayment,
  useInitUtmSource,
  useInitUtmSourceLauncher,
  useInitUtmTools,
} from '@shared/lib/utm'
import { reqHeaders } from '@shared/config/constants'

interface FetchQR {
  qrcId: string
  payload: string
}

export const useFetchQR = (id: string) => {
  const zone = getPcekZone()
  const { partner } = usePartner()

  const { isWebView } = useWebView()
  const { utmSource } = useInitUtmSource()
  const { utmSourceLauncher } = useInitUtmSourceLauncher()
  const { isMembrana } = useInitUtmMembrana()
  const { isMtsDengi } = useInitUtmMtsDengi()
  const { isTools } = useInitUtmTools()
  const { isColizeum } = useInitUtmColizeum()
  const { isFromPayment } = useInitUtmPayment()
  const { isMkSmsGames } = useInitUtmIsMkSmsGames()

  let FhpSessionId
  let FhpRequestId

  if (typeof window !== 'undefined') {
    FhpSessionId = window?.gibSessionId
    FhpRequestId = window?.gibRequestId
  }

  const headers = {
    'X-Request-ID': id,
    'x-system-id': getXSystemId({
      partner,
      isMembrana,
      isWebView,
      utmSource,
      utmSourceLauncher,
      isMtsDengi,
      isTools,
      isColizeum,
      isFromPayment,
      isMkSmsGames,
    }),
    ...reqHeaders,
    FhpSessionId,
    FhpRequestId,
  }

  const sendQrCheckMetric = (payload: Record<string, unknown>) => {
    const body = JSON.stringify(payload)

    if (typeof window === 'undefined') {
      return
    }

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' })
      const sent = navigator.sendBeacon('/cyber/api/metrics/qr-check', blob)

      if (sent) {
        return
      }
    }

    void fetch('/cyber/api/metrics/qr-check', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    })
  }

  const fetchQR = async (config?: { data?: unknown }) => {
    const start = Date.now()

    try {
      // Запрос без cancel token: повторный клик и ререндер не должны его отменять.
      const res = await axios.post<FetchQR>(`${baseUmpPath}/${zone}/qr-transfer/phub/check/qr`, config?.data, {
        headers,
      })

      sendQrCheckMetric({
        status: 'success',
        statusCode: res.status,
        statusText: res.statusText,
        durationMs: Date.now() - start,
        zone,
      })

      return res
    } catch (e) {
      const err = e as {
        response?: { status?: number; statusText?: string; data?: { code?: number; message?: string } }
        request?: unknown
        code?: string
        name?: string
        message?: string
        config?: unknown
      }

      const hasResponse = Boolean(err?.response)
      const hasRequest = Boolean(err?.request) && !hasResponse
      const axiosCode = err?.code
      const isTimeout = axiosCode === 'ECONNABORTED'
      const isCanceled = typeof axios.isCancel === 'function' && axios.isCancel(e)
      const isNetworkError = !hasResponse && hasRequest

      let errorKind = 'unknown_error'

      if (isCanceled) {
        errorKind = 'canceled'
      } else if (isTimeout) {
        errorKind = 'timeout'
      } else if (isNetworkError) {
        errorKind = 'network_error'
      } else if (hasResponse) {
        errorKind = 'http_error'
      }

      sendQrCheckMetric({
        status: 'error',
        statusCode: err?.response?.status,
        statusText: err?.response?.statusText,
        durationMs: Date.now() - start,
        zone,
        vendorCode: err?.response?.data?.code,
        vendorMessage: err?.response?.data?.message,
        // дополнительные диагностические поля для кейсов без status
        errorKind,
        axiosCode,
        errorName: err?.name,
        errorMessage: err?.message,
      })

      throw e
    }
  }

  return { fetchQR, isWebView, FhpSessionId }
}
